const UTM_KEY = 'river_utm_params';

/**
 * Per-tab sessionStorage key written by content-renderer's `_app.tsx` on
 * every page mount (first-touch-wins). When the SPA boots on /signup after
 * a CR→SPA handoff, this is the most reliable source for first_landing_page
 * because it:
 *   - Survives Referrer-Policy stripping
 *   - Works for static SSR-rendered <a href> links (no URL param needed)
 *   - Captures FIRST-touch, not last-touch (referrer / landing_path only
 *     know the immediately-prior page).
 *
 * Kept in sync with the CR-side constant in
 * `content-renderer/lib/spaHandoff.ts` (LANDING_PATH_SESSION_KEY). meetriver.com's
 * content-renderer container shares this origin with the SPA, so sessionStorage
 * is shared there. rivereditor.com's content-renderer container (legacy
 * blogs/guides/tools) is a different origin from the SPA, same as local dev
 * (CR :3001 / SPA :3000) - both fall back to the `?landing_path=` URL param
 * `spaHandoff.buildHandoffUrl` always sets alongside `sessionStorage`.
 */
const LANDING_PATH_SESSION_KEY = 'river_first_landing_path';

/**
 * Read directly off the current URL's query string below (not just
 * localStorage), which is what makes UTM capture work across the
 * rivereditor.com -> meetriver.com handoff: `spaHandoff.ts`'s
 * `appendMarketingAttribution` forwards these explicitly as URL params on
 * every CR->SPA handoff specifically because CR's own `localStorage` write
 * (`rememberAcquisitionParams`) lives on CR's origin and is unreachable once
 * the SPA is a different origin (rivereditor.com content pages handing off to
 * meetriver.com).
 */
const UTM_PARAM_NAMES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  // Optional: undefined when the user's first SPA page was an auth/conversion
  // page (e.g. /signup after a CR→SPA handoff) and no real source could be
  // recovered. Use upgradeLandingPageIfDedicated to fill it in later.
  first_landing_page?: string;
  referrer?: string;
  captured_at: string;
}

/**
 * Normalize a pathname for storage: collapse a trailing slash so that
 * "/login" and "/login/" are recorded as the same path. The root "/" itself
 * is preserved.
 */
function normalizeLandingPath(pathname: string): string {
  if (!pathname) return pathname;
  if (pathname === '/') return pathname;
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * Pages that should never claim signup attribution: auth pages, admin shell,
 * logout. A user transiting through `/signup` after starting on `/` did not
 * "land" on `/signup` — that's the conversion destination, not the entry
 * point.
 */
function isExcludedFromAttribution(pathname: string): boolean {
  return (
    pathname === '/signup' || pathname.startsWith('/signup/') ||
    pathname === '/login' || pathname.startsWith('/login/') ||
    pathname === '/logout' || pathname.startsWith('/logout/') ||
    pathname === '/admin' || pathname.startsWith('/admin/') ||
    pathname.startsWith('/auth/') || pathname === '/auth'
  );
}

/**
 * Resolve the first attributable landing path the user visited, in priority
 * order:
 *
 *   1. sessionStorage (`LANDING_PATH_SESSION_KEY`). Written by content-
 *      renderer's `_app.tsx` on every CR page mount with first-touch-wins
 *      semantics. Survives Referrer-Policy stripping, works for static SSR
 *      <a href> CTAs, and captures the true first-touch within the tab.
 *      Shared with the SPA on production (same hostname).
 *
 *   2. `?landing_path=` query param. Written by `spaHandoff.buildHandoffUrl`
 *      on imperative CTA clicks. Covers the cross-origin dev case where CR
 *      (:3001) and SPA (:3000) don't share sessionStorage.
 *
 *   3. The current pathname, if not excluded. Covers the case where the SPA
 *      itself is the marketing surface (e.g. SPA-owned marketing pages).
 *
 *   4. `document.referrer` pathname, if same-origin and not excluded. Last-
 *      ditch fallback for static SSR <a href> links from CR when
 *      sessionStorage wasn't populated (e.g. user opened /signup in a new
 *      tab with `target="_blank"` and the new-tab session has no prior CR
 *      page view).
 *
 *   5. `undefined`. Recorded as "no attribution" in the admin dashboard,
 *      which is correct — we'd rather under-attribute than mis-attribute to
 *      `/signup`.
 */
function resolveFirstLandingPage(urlParams: URLSearchParams): string | undefined {
  // 1. sessionStorage from CR
  try {
    const stored = window.sessionStorage.getItem(LANDING_PATH_SESSION_KEY);
    if (stored && stored.startsWith('/') && !isExcludedFromAttribution(stored)) {
      return normalizeLandingPath(stored);
    }
  } catch {
    // sessionStorage unavailable — fall through
  }

  // 2. ?landing_path= URL param
  const landingPathParam = urlParams.get('landing_path');
  if (
    landingPathParam &&
    landingPathParam.startsWith('/') &&
    !isExcludedFromAttribution(landingPathParam)
  ) {
    return normalizeLandingPath(landingPathParam);
  }

  // 3. Current pathname if not excluded
  const pathname = normalizeLandingPath(window.location.pathname);
  if (!isExcludedFromAttribution(pathname)) {
    return pathname;
  }

  // 4. document.referrer (same-origin only)
  try {
    const ref = document.referrer;
    if (ref) {
      const refUrl = new URL(ref);
      if (
        (refUrl.hostname === window.location.hostname ||
          refUrl.hostname === 'rivereditor.com' ||
          refUrl.hostname === 'meetriver.com') &&
        !isExcludedFromAttribution(refUrl.pathname)
      ) {
        return normalizeLandingPath(refUrl.pathname);
      }
    }
  } catch {
    // Malformed referrer — fall through
  }

  // 5. undefined
  return undefined;
}

/**
 * A stored `first_landing_page` value is "stale" — eligible to be re-resolved
 * or overwritten — when it is missing, the homepage, or itself an excluded
 * auth/conversion path. The third case catches polluted localStorage from
 * pre-fix visits where /signup, /login, etc. were incorrectly recorded.
 */
function isStaleFirstLandingPage(value: string | undefined): boolean {
  return !value || value === '/' || isExcludedFromAttribution(value);
}

/**
 * Capture the first landing page (and UTM params if present).
 *
 * First-touch-wins for a real dedicated path: once `first_landing_page` is a
 * non-excluded path other than `/`, we never overwrite it.
 *
 * If localStorage already holds a stale value (missing, `/`, or an excluded
 * auth path from pre-fix visits) we re-resolve via the same priority chain
 * `resolveFirstLandingPage` uses and update the stored entry in place. This
 * is what cleans up the long tail of pre-fix users whose localStorage was
 * polluted with `/signup` / `/login` and never got a chance to be upgraded
 * (they go straight to /signup → OAuth → /api/auth/complete, never visiting
 * a dedicated SPA route where `upgradeLandingPageIfDedicated` would fire).
 *
 * See `resolveFirstLandingPage` for the source priority chain.
 */
export function captureUtmParams(): void {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const existing = localStorage.getItem(UTM_KEY);

    if (existing) {
      let parsed: UtmParams | null = null;
      try {
        parsed = JSON.parse(existing) as UtmParams;
      } catch {
        // Malformed JSON — fall through to fresh capture below
      }
      if (parsed) {
        if (!isStaleFirstLandingPage(parsed.first_landing_page)) {
          // Real dedicated path already captured — preserve first-touch-wins
          return;
        }
        const recovered = resolveFirstLandingPage(urlParams);
        if (recovered && recovered !== parsed.first_landing_page) {
          const prev = parsed.first_landing_page;
          parsed.first_landing_page = recovered;
          localStorage.setItem(UTM_KEY, JSON.stringify(parsed));
          console.info(`[UTM] Recovered first_landing_page: ${prev ?? '(none)'} → ${recovered}`);
        }
        return;
      }
    }

    const firstLandingPage = resolveFirstLandingPage(urlParams);

    const captured: UtmParams = {
      first_landing_page: firstLandingPage,
      captured_at: new Date().toISOString(),
    };

    for (const key of UTM_PARAM_NAMES) {
      const val = urlParams.get(key);
      if (val) captured[key] = val;
    }

    const ref = document.referrer;
    if (ref) captured.referrer = ref;

    localStorage.setItem(UTM_KEY, JSON.stringify(captured));
    console.info(`[UTM] Captured: utm_source=${captured.utm_source ?? '(none)'} referrer=${captured.referrer ?? '(none)'} landing_page=${captured.first_landing_page ?? '(none)'}`);
  } catch {
    // localStorage unavailable (private browsing edge cases) — silently skip
  }
}

/**
 * Upgrade the stored landing page when the user navigates to a dedicated path
 * within the SPA. Overwrites a stale stored value (missing, `/`, or an
 * excluded auth path) with the new dedicated path. Real dedicated paths are
 * preserved — first dedicated wins.
 *
 * Auth/admin/logout paths are excluded as the upgrade source: they're
 * conversion destinations, not landing pages, and must not claim attribution
 * from the real entry point.
 */
export function upgradeLandingPageIfDedicated(pathname: string): void {
  try {
    if (!pathname || pathname === '/') return;
    if (isExcludedFromAttribution(pathname)) return;
    const stored = localStorage.getItem(UTM_KEY);
    if (!stored) return;
    const parsed = JSON.parse(stored) as UtmParams;
    if (!isStaleFirstLandingPage(parsed.first_landing_page)) return;
    const normalized = normalizeLandingPath(pathname);
    const prev = parsed.first_landing_page;
    parsed.first_landing_page = normalized;
    localStorage.setItem(UTM_KEY, JSON.stringify(parsed));
    console.info(`[UTM] Upgraded landing page from ${prev ?? '(none)'} to ${normalized}`);
  } catch {
    // localStorage unavailable or JSON malformed — silently skip.
  }
}

/**
 * Return the stored UTM params, or null if none were captured.
 */
export function getStoredUtmParams(): UtmParams | null {
  try {
    const stored = localStorage.getItem(UTM_KEY);
    return stored ? (JSON.parse(stored) as UtmParams) : null;
  } catch {
    return null;
  }
}

/**
 * Clear stored UTM params. Call after confirmed successful write to backend.
 */
export function clearUtmParams(): void {
  try {
    localStorage.removeItem(UTM_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Affiliate ref code capture — first-touch-wins, parallel to UTM params
// ---------------------------------------------------------------------------

const REF_KEY = 'river_ref_code';

/**
 * Capture ?ref= query param on first visit (first-touch-wins, never overwrites).
 * Also called by the /r/:code route component which injects the code directly.
 */
export function captureRefCode(explicitCode?: string): void {
  try {
    if (localStorage.getItem(REF_KEY)) return;
    const code = explicitCode ?? new URLSearchParams(window.location.search).get('ref');
    if (code) {
      const normalized = code.toUpperCase().trim();
      localStorage.setItem(REF_KEY, normalized);
      console.info(`[Affiliate] Captured ref code: ${normalized}`);
    }
  } catch {
    // localStorage unavailable (private browsing edge cases) — silently skip
  }
}

/**
 * Return the stored affiliate ref code, or null if none was captured.
 */
export function getStoredRefCode(): string | null {
  try {
    return localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear stored affiliate ref code. Call after conversion is confirmed.
 */
export function clearRefCode(): void {
  try {
    localStorage.removeItem(REF_KEY);
  } catch {
    // ignore
  }
}
